/**
 * FormResponsesFilters — barre de filtres du registre des réponses (9.U.7).
 *
 * Contrôlé par l'URL (query string = source de vérité, filtres partageables). Les
 * valeurs viennent du parent (dérivées de `useSearchParams`) ; chaque changement
 * remonte via `onChange(patch)`. Formulaire → versions publiées dépendantes.
 */
import type { Schemas } from "@kollectivia/shared";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { z } from "zod";

import { ProjectPicker } from "../../../components/ProjectPicker";
import { useFormTemplates, useFormTemplateVersions } from "../../../lib/queries/formTemplates";

type Filters = {
  status?: string;
  submitted_from?: string;
  template_id?: string;
  template_version_id?: string;
  project_id?: string;
  from?: string;
  to?: string;
};

type Props = {
  value: Filters;
  onChange: (patch: Partial<Filters>) => void;
};

const STATUSES = ["draft", "submitted", "locked"] as const;
const SOURCES: z.infer<typeof Schemas.FormResponseSubmitSourceSchema>[] = [
  "public_magic_link",
  "broker_ui",
  "api",
  "mcp",
  "preview",
];

const selectCls =
  "mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500";
const labelCls = "block text-xs font-medium text-slate-600";

export function FormResponsesFilters({ value, onChange }: Props) {
  const { t } = useTranslation();

  const templatesQ = useFormTemplates({ status: "active" });
  const templates = templatesQ.data?.items ?? [];

  const versionsQ = useFormTemplateVersions(value.template_id || undefined);
  const publishedVersions = useMemo(
    () =>
      (versionsQ.data?.items ?? [])
        .filter((v) => v.published_at !== null)
        .sort((a, b) => b.version_number - a.version_number),
    [versionsQ.data],
  );

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <div>
        <label htmlFor="fr-f-status" className={labelCls}>
          {t("form_responses.filters.status")}
        </label>
        <select
          id="fr-f-status"
          value={value.status ?? ""}
          onChange={(e) => onChange({ status: e.target.value || undefined })}
          className={selectCls}
        >
          <option value="">{t("form_responses.filters.all")}</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`form_responses.status.${s}`)}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="fr-f-source" className={labelCls}>
          {t("form_responses.filters.source")}
        </label>
        <select
          id="fr-f-source"
          value={value.submitted_from ?? ""}
          onChange={(e) => onChange({ submitted_from: e.target.value || undefined })}
          className={selectCls}
        >
          <option value="">{t("form_responses.filters.all")}</option>
          {SOURCES.map((s) => (
            <option key={s} value={s}>
              {t(`form_responses.source.${s}`)}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="fr-f-template" className={labelCls}>
          {t("form_responses.filters.template")}
        </label>
        <select
          id="fr-f-template"
          value={value.template_id ?? ""}
          onChange={(e) =>
            // Changer de formulaire réinitialise la version sélectionnée.
            onChange({ template_id: e.target.value || undefined, template_version_id: undefined })
          }
          className={selectCls}
        >
          <option value="">{t("form_responses.filters.all")}</option>
          {templates.map((tpl) => (
            <option key={tpl.id} value={tpl.id}>
              {tpl.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="fr-f-version" className={labelCls}>
          {t("form_responses.filters.version")}
        </label>
        <select
          id="fr-f-version"
          value={value.template_version_id ?? ""}
          disabled={!value.template_id || publishedVersions.length === 0}
          onChange={(e) => onChange({ template_version_id: e.target.value || undefined })}
          className={`${selectCls} disabled:bg-slate-50 disabled:text-slate-400`}
        >
          <option value="">{t("form_responses.filters.all")}</option>
          {publishedVersions.map((v) => (
            <option key={v.id} value={v.id}>
              {t("form_responses.filters.version_option", { number: v.version_number })}
            </option>
          ))}
        </select>
      </div>

      <div className="lg:col-span-2">
        <label className={labelCls}>{t("form_responses.filters.project")}</label>
        <ProjectPicker value={value.project_id} onChange={(id) => onChange({ project_id: id })} />
      </div>

      <div>
        <label htmlFor="fr-f-from" className={labelCls}>
          {t("form_responses.filters.from")}
        </label>
        <input
          id="fr-f-from"
          type="date"
          value={value.from ? value.from.slice(0, 10) : ""}
          onChange={(e) =>
            onChange({ from: e.target.value ? `${e.target.value}T00:00:00.000Z` : undefined })
          }
          className={selectCls}
        />
      </div>

      <div>
        <label htmlFor="fr-f-to" className={labelCls}>
          {t("form_responses.filters.to")}
        </label>
        <input
          id="fr-f-to"
          type="date"
          value={value.to ? value.to.slice(0, 10) : ""}
          onChange={(e) =>
            onChange({ to: e.target.value ? `${e.target.value}T23:59:59.999Z` : undefined })
          }
          className={selectCls}
        />
      </div>
    </div>
  );
}
